import { DateTime, Duration } from 'luxon';
import { action, makeAutoObservable } from 'mobx';
import { EntityApiResponse } from '../../api/EntityApi';
import {
  BillingDelegateApiResponse,
  GroupApiResponse,
  GroupsApi,
  GroupUpdateApiRequest,
  GroupUserResponse,
  MoveEntitiesResponse,
} from '../../api/GroupApi';
import { CalculationApi } from '../../api/CalculationApi';
import { UserPermission } from '../../api/UserApi';
import { Calculation } from './Calculation';
import { Entity } from './Entity';
import { Payment } from './Payment';
import { Subscription } from './Subscription';
import { User } from './User';
import { SourceApi } from '../../api/SourceApi';
import { BalancerSettings } from './BalancerSettings';
import { Plan } from '../../api/BillingApi';
import { BillingFrequency } from '../../config/Constants';
import { Limits } from './Limits';

export enum BillingStatus {
  TRIAL = 'trialing',
  ACTIVE = 'active',
  PAUSED = 'paused',
  CANCELLED = 'deleted',
  PAST_DUE = 'past_due',
}

export type GroupUser = {
  user: User;
  permission: UserPermission;
};

export class Group {
  id: number;
  entities: Entity[] = [];
  archivedEntities: Entity[] = [];
  name: string = '';
  billingStatus: BillingStatus = BillingStatus.TRIAL;
  trialEnd: DateTime;
  startDate: DateTime = DateTime.now().minus(
    Duration.fromObject({ months: 6 })
  );

  team: GroupUser[] = [];
  connectionOk: boolean = true;
  hasOnboarded: boolean = false;

  loadedSubscription: boolean = false;
  subscription: Subscription = null;
  is202310PlanOverride: boolean = false;
  payments: Payment[] = [];
  billingDelegate: BillingDelegateApiResponse = null;
  limits: Limits = null;

  loaded: boolean = false;
  apiOrganisations: EntityApiResponse[] = [];
  archivedApiOrganisations: EntityApiResponse[] = [];

  _syncInProgress: boolean = false;

  balancerSettings: BalancerSettings = new BalancerSettings();

  // Feature flags
  hasBrag: boolean = false;
  hasRecharger: boolean = false;
  hasBalancer: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: GroupApiResponse): Group {
    const group = new Group();
    group.loadFromApi(apiData);
    return group;
  }

  loadFromApi = (apiData: GroupApiResponse): void => {
    this.id = apiData.id;
    this.name = apiData.name;
    this.hasOnboarded = apiData.hasOnboarded;
    this.billingStatus = apiData.billingStatus;
    this.trialEnd = DateTime.fromISO(apiData.trialEnd);
    this.startDate = DateTime.fromISO(apiData.startDate);
    this.loaded = false;
    this.apiOrganisations = (apiData.organisations || []).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    this.archivedApiOrganisations = apiData.archivedOrganisations;
    this.balancerSettings = BalancerSettings.fromApi(apiData.balancerSettings);
    this.hasBrag = apiData.hasBrag;
    this.hasRecharger = apiData.hasRecharger;
    this.hasBalancer = apiData.hasLoans;
    this.is202310PlanOverride = apiData.is202310PlanOverride ?? false;
  };

  toApi = (): GroupUpdateApiRequest => ({
    name: this.name,
    hasOnboarded: this.hasOnboarded,
    startDate: this.startDate.toISODate(),
    balancerSettings: this.balancerSettings.toApi(),
    hasBrag: this.hasBrag,
    hasRecharger: this.hasRecharger,
    hasLoans: this.hasBalancer,
  });

  createCalculation = (start: string, end: string): Promise<Calculation> =>
    CalculationApi.create(this.id, start, end).then(response =>
      Calculation.fromApi(response)
    );

  loadTeam = (force: boolean = false): Promise<GroupUser[]> => {
    if (this.team.length === 0 || force) {
      return GroupsApi.getUsers(this.id).then((users: GroupUserResponse[]) => {
        this.team = users.map(u => ({
          user: User.fromApi(u.user),
          permission: u.permission,
        }));
        return this.team;
      });
    }
    return Promise.resolve(this.team);
  };

  invite = (email: string): Promise<GroupUser[]> => {
    return GroupsApi.invite(this.id, email).then(() => {
      return this.loadTeam(true);
    });
  };

  removeUser = (groupId: number, userId: number): Promise<GroupUser[]> => {
    return GroupsApi.removeUser(groupId, userId).then(() =>
      this.loadTeam(true)
    );
  };

  save = (): Promise<Group> => {
    return GroupsApi.update(this.id, this.toApi()).then(() => {
      return this;
    });
  };

  updateTrialEnd = (trialEnd: string): Promise<Group> => {
    return GroupsApi.updateTrialEnd(this.id, trialEnd).then(response => {
      this.trialEnd = DateTime.fromISO(response.trialEnd);
      return this;
    });
  };

  getEntity = (id: number): Entity => {
    return this.entities.find(e => e.id === id);
  };

  archiveEntity = (entity: Entity): Promise<void> => {
    return GroupsApi.archiveEntity(this.id, entity.id).then(() => {
      this.entities = this.entities.filter(e => e.id !== entity.id);
      this.archivedEntities.push(entity);
    });
  };

  restoreEntity = (entity: Entity): Promise<void> => {
    return GroupsApi.restoreEntity(this.id, entity.id).then(() => {
      this.archivedEntities = this.archivedEntities.filter(
        e => e.id !== entity.id
      );
      this.entities.push(entity);
    });
  };

  deleteEntity = (entityId: number): Promise<void> => {
    return GroupsApi.removeEntity(this.id, entityId).then(() => {
      this.entities = this.entities.filter(entity => entity.id !== entityId);
    });
  };

  moveEntities = (
    newGroup: Group,
    entityIds: number[],
    dryRun: Boolean
  ): Promise<MoveEntitiesResponse> => {
    return GroupsApi.moveEntities(this.id, entityIds, newGroup.id, dryRun).then(
      response => {
        if (!dryRun) {
          this.entities = this.entities.filter(
            entity => !entityIds.includes(entity.id)
          );
        }
        return response;
      }
    );
  };

  get hasNameSet(): boolean {
    return this.name !== '' && this.name !== 'Default';
  }

  get trialDaysLeft(): number {
    return Math.round(this.trialEnd.diffNow('days').days);
  }

  get hasCurrentSubscription(): boolean {
    return (
      ![BillingStatus.TRIAL, BillingStatus.CANCELLED].includes(
        this.billingStatus
      ) && this.subscription !== null
    );
  }

  get isSubscriptionCancelledButStillActive(): boolean {
    return (
      this.hasCurrentSubscription &&
      this.subscription.cancellationDate > DateTime.now()
    );
  }

  get inTrialOrSubscribed(): boolean {
    return (
      (this.billingStatus === BillingStatus.TRIAL && this.trialDaysLeft > 0) ||
      this.billingStatus === BillingStatus.ACTIVE
    );
  }

  get inTrialOrHighTierPlan(): boolean {
    this.loadSubscription();
    return (
      (this.billingStatus === BillingStatus.TRIAL && this.trialDaysLeft > 0) ||
      this.subscription?.plan === Plan.full ||
      this.subscription?.plan === Plan.active ||
      this.subscription?.plan === Plan.advanced
    );
  }

  get haveAllCompletedDataImport(): boolean {
    return this.entities
      .map(entity => entity.hasCompletedDataImport)
      .reduce((a, b) => a && b, true);
  }

  get syncInProgress(): boolean {
    return this._syncInProgress;
  }

  set syncInProgress(v: boolean) {
    this._syncInProgress = v;
  }

  loadSubscription = (force: boolean = false): Promise<Group> => {
    if (this.loadedSubscription && !force) {
      return Promise.resolve(this);
    }
    this.loadedSubscription = true;

    return GroupsApi.getSubscription(this.id).then(response => {
      this.subscription = response.subscription
        ? Subscription.fromApi(response.subscription)
        : null;
      this.payments = response.payments
        ? response.payments.map(p => Payment.fromApi(p))
        : [];
      this.billingDelegate = response.billingDelegate
        ? response.billingDelegate
        : null;
      this.limits = response.limits ? Limits.fromApi(response.limits) : null;
      return this;
    });
  };

  updateSubscription = (
    plan: Plan,
    billingFrequency: BillingFrequency,
    rechargerAddonCount: number = 0,
    bragAddonCount: number = 0
  ): Promise<Group> => {
    return GroupsApi.updateSubscription(
      this.id,
      plan,
      billingFrequency,
      rechargerAddonCount,
      bragAddonCount
    ).then(response => {
      this.subscription = Subscription.fromApi(response);
      return this;
    });
  };

  updatePaddleSubscriptionUrl = async (): Promise<string> => {
    return (await GroupsApi.getSubscriptionManagementUrls(this.id))
      .updatePaymentMethod;
  };

  cancelPaddleSubscriptionUrl = async (): Promise<string> => {
    return (await GroupsApi.getSubscriptionManagementUrls(this.id)).cancel;
  };

  getPaddleInvoiceUrl = async (paymentId: number): Promise<string> => {
    return (await GroupsApi.getSubscriptionInvoice(this.id, paymentId)).url;
  };

  loadSources = (): Promise<Group> => {
    return SourceApi.getGroupSources(this.id).then(
      action(sourcesResponse => {
        this.entities.forEach(entity => {
          if (sourcesResponse[entity.id]) {
            entity.setSources(sourcesResponse[entity.id]);
          }
        });
        return this;
      })
    );
  };

  isPaddleClassic = () => {
    return this.is202310PlanOverride || this.subscription?.isPaddleClassic();
  };
}
